import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import {
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import Link from "@mui/material/Link";
import makeStyles from "@mui/styles/makeStyles";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import LoadingButton from "../../../components/LoadingButton";
import { callAPI } from "../../../lib/common";
import Button from "@mui/material/Button";
import BoxActions from "./BoxActions";
import { useAuth } from "../../providers/AuthProvider";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";
import FormikTextField from "../../../components/Common/FormikTextField";
import Level2BoxContainer from "./Level2BoxContainer";
import Level2BoxHeading from "./Level2BoxHeading";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
  },
  form: {
    flexGrow: 1,
    marginBottom: 0,
  },
}));

const Login = ({ email, handleGoBack, handleLoginSuccess }) => {
  const { t } = useTranslation();
  const { isAuthenticating, loginError, login: authLogin } = useAuth();
  const classes = useStyles();
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState();
  const isMobile = useIsMobileDevice();

  const openSendForgotPasswordDialog = (e) => {
    e.preventDefault();
    setForgotPasswordDialogOpen(true);
  };

  console.log("Login with email", email);

  return (
    <Level2BoxContainer>
      <Level2BoxHeading>
        {t("Welcome back, %s", { postProcess: "sprintf", sprintf: [email] })}
      </Level2BoxHeading>

      <Box mb={1} sx={{ textAlign: "center" }}>
        <Trans>Please enter your password</Trans>
      </Box>

      <Formik
        initialValues={{
          password: "",
        }}
        validationSchema={loginSchema(t)}
        onSubmit={(values, { setSubmitting, setErrors }) => {
          authLogin(email, values.password)
            .then((result) => {
              if (
                window.isEpmsAnalyticsFeatureEnabled &&
                window.epmsAnalytics
              ) {
                window.epmsAnalytics.onLogin(result.user);
              }

              handleLoginSuccess(result);
            })
            .catch(() => {});
        }}
      >
        {({ submitForm, isValid, errors }) => {
          return (
            <Form className={classes.form}>
              <Field
                id="new-subject-code-password"
                component={FormikTextField}
                type="password"
                label={t("Password")}
                name="password"
                variant="outlined"
                fullWidth
                autoFocus
              />

              <Box
                className={classes.subText}
                mt={1}
                mb={4}
                display="flex"
                justifyContent="flex-end"
              >
                <Link href="#" onClick={openSendForgotPasswordDialog}>
                  <Trans>Forgot your password?</Trans>
                </Link>{" "}
              </Box>

              <FormHelperText error={!!loginError}>
                {loginError}
              </FormHelperText>

              <BoxActions>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGoBack}
                  size={isMobile ? "medium" : "large"}
                >
                  <Trans>Go back</Trans>
                </Button>
                <LoadingButton
                  variant="primary"
                  isLoading={isAuthenticating}
                  onClick={submitForm}
                  disabled={!isValid}
                  fullWidth
                  size={isMobile ? "medium" : "large"}
                >
                  <Trans>Log in</Trans>
                </LoadingButton>
              </BoxActions>
              {forgotPasswordDialogOpen && (
                <ForgotPasswordDialog
                  email={email}
                  onClose={() => setForgotPasswordDialogOpen(false)}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </Level2BoxContainer>
  );
};

const ForgotPasswordDialog = ({ email, onClose }) => {
  const [sending, setSending] = useState(true);
  const [error, setError] = useState(null);
  const [completeMessage, setCompleteMessage] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    callAPI(
      `/sections/prophoto/ajax/send_password_reminder.php?json=1&email=${email}`,
    )
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          setCompleteMessage(res.message);
        }
      })
      .catch((err) => {
        setError(t("Error sending password reset instructions."));
      })
      .finally(() => {
        setSending(false);
      });
  }, []);

  const getContent = () => {
    if (sending) {
      return (
        <DialogContent>
          <Trans>Sending...</Trans>
        </DialogContent>
      );
    }
    if (error || completeMessage) {
      return (
        <>
          <DialogContent>{error || completeMessage}</DialogContent>
          <DialogActions>
            <Button color="primary" onClick={onClose}>
              <Trans>Ok</Trans>
            </Button>
          </DialogActions>
        </>
      );
    }
  };

  return <Dialog open={true}>{getContent()}</Dialog>;
};

const loginSchema = (t) => () => {
  return Yup.object().shape({
    password: Yup.string()
      .min(3, t("That's too short!"))
      .required(t("This is required!")),
  });
};

export default Login;
